'use client';

import React, { useState, useRef, useEffect, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from './button';
import {
  fieldControlClassName,
  fieldDensityClassName,
  type FieldControlSize,
  type FieldDensity,
} from './input';
import { useFloatingListboxPosition } from './use-floating-listbox-position';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  | 'aria-describedby'
  | 'aria-invalid'
  | 'aria-label'
  | 'aria-required'
  | 'children'
  | 'className'
  | 'disabled'
  | 'id'
  | 'name'
  | 'onBlur'
  | 'onChange'
  | 'onClick'
  | 'onKeyDown'
  | 'role'
  | 'size'
  | 'type'
  | 'value'
> {
  options: SelectOption[];
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  density?: FieldDensity;
  /** Productive controls use 44px form, 36px toolbar, or 32px inline-editor slots. */
  size?: FieldControlSize;
  error?: boolean;
  disabled?: boolean;
  name?: string;
  required?: boolean;
  id?: string;
  'aria-label'?: string;
  /**
   * Forwarded onto the inner combobox <button> so callers can associate
   * an external warning/help message with the focused control. Added
   * for W-D (staff-onboarding role→award warning) — non-breaking.
   */
  'aria-describedby'?: string;
  'aria-invalid'?: React.AriaAttributes['aria-invalid'];
  'aria-required'?: React.AriaAttributes['aria-required'];
}

export function Select({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  className,
  density,
  error = false,
  disabled = false,
  name,
  required = false,
  id,
  size = 'default',
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  'aria-required': ariaRequired,
  ...triggerProps
}: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listboxRef = useRef<HTMLUListElement>(null);
  const listboxId = useId();
  const { position } = useFloatingListboxPosition({ isOpen, triggerRef });

  const selectedOption = options.find((o) => o.value === value);

  const close = useCallback(() => {
    setIsOpen(false);
    setHighlightedIndex(-1);
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(e: MouseEvent | FocusEvent) {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (listboxRef.current?.contains(target)) return;
      close();
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('focusin', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('focusin', handleClickOutside);
    };
  }, [isOpen, close]);

  useEffect(() => {
    if (highlightedIndex >= 0 && listboxRef.current) {
      const items = listboxRef.current.querySelectorAll('[role="option"]');
      items[highlightedIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlightedIndex]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;
    switch (e.key) {
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (isOpen && highlightedIndex >= 0) {
          const opt = options[highlightedIndex];
          if (opt && !opt.disabled) {
            onChange?.(opt.value);
            close();
          }
        } else {
          setIsOpen(true);
        }
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
        } else {
          setHighlightedIndex((prev) => {
            let next = prev + 1;
            while (next < options.length && options[next]?.disabled) next++;
            return next < options.length ? next : prev;
          });
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (isOpen) {
          setHighlightedIndex((prev) => {
            let next = prev - 1;
            while (next >= 0 && options[next]?.disabled) next--;
            return next >= 0 ? next : prev;
          });
        }
        break;
      case 'Escape':
        if (isOpen) {
          e.stopPropagation();
          close();
        }
        break;
      case 'Tab':
        close();
        break;
    }
  };

  const listbox =
    isOpen && position && typeof document !== 'undefined'
      ? createPortal(
          <ul
            id={listboxId}
            ref={listboxRef}
            role="listbox"
            style={{
              position: 'fixed',
              top: position.top,
              left: position.left,
              width: position.width,
              maxHeight: position.maxHeight,
              // A dropdown must render above whatever opened it.
              //
              // Owner, 2026-08-26, on live production: "sitewide that dropdowns
              // are not visible", "I hit the dropdown issue on many surfaces",
              // "a lot of them were on popups such as the log time wizard".
              //
              // This panel portals to document.body with position:fixed, which
              // takes it out of every clipping ancestor - that part was right. But
              // it sat at z-index 80 while dialog.tsx uses z-[200], the create-shift
              // host z-[210] and date-entry z-[220]. So inside ANY modal the panel
              // opened behind the modal: present in the DOM, present in the
              // accessibility tree, invisible on screen. The Log Time wizard is
              // exactly that shape.
              //
              // 300 sits above every overlay in the app. A transient, dismissible
              // popover belongs at the top of the stack by definition - it is not
              // competing with the modal, it is owned by it.
              zIndex: 300,
              transformOrigin: position.placement === 'top' ? 'bottom' : 'top',
            }}
            className="overflow-auto rounded-overlay border border-border bg-surface-sheet py-1 shadow-popover"
          >
            {options.length === 0 ? (
              <li className="px-4 py-2 text-sm text-text-muted">No options</li>
            ) : (
              options.map((opt, idx) => (
                <li
                  key={opt.value}
                  id={`${listboxId}-option-${idx}`}
                  role="option"
                  aria-selected={opt.value === value}
                  aria-disabled={opt.disabled}
                  // Options use virtual focus; preserve the trigger until selection.
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => !opt.disabled && setHighlightedIndex(idx)}
                  onClick={() => {
                    if (!opt.disabled) {
                      onChange?.(opt.value);
                      close();
                    }
                  }}
                  className={cn(
                    'flex items-center justify-between px-4 py-2 font-body text-sm cursor-pointer transition-interactive',
                    idx === highlightedIndex && 'bg-mist',
                    opt.value === value && 'text-action font-medium',
                    opt.disabled && 'cursor-not-allowed opacity-50',
                    !opt.disabled && 'text-text-heading',
                  )}
                >
                  <span className="truncate">{opt.label}</span>
                  {opt.value === value && <Check className="ml-2 h-4 w-4 shrink-0 text-action" />}
                </li>
              ))
            )}
          </ul>,
          document.body,
        )
      : null;

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {(name || required) && (
        <input
          type="text"
          name={name}
          value={value ?? ''}
          required={required}
          tabIndex={-1}
          aria-hidden="true"
          className="sr-only"
          onChange={() => {}}
        />
      )}
      <Button
        variant="outline"
        size={size}
        density={density}
        {...triggerProps}
        ref={triggerRef}
        type="button"
        id={id}
        role="combobox"
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={isOpen ? listboxId : undefined}
        aria-activedescendant={
          listbox && highlightedIndex >= 0 && options[highlightedIndex]
            ? `${listboxId}-option-${highlightedIndex}`
            : undefined
        }
        aria-describedby={ariaDescribedBy}
        aria-invalid={error || ariaInvalid}
        aria-required={required || ariaRequired}
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        onBlur={(event) => {
          const target = event.relatedTarget;
          if (target instanceof Node && listboxRef.current?.contains(target)) return;
          close();
        }}
        className={cn(
          fieldControlClassName,
          fieldDensityClassName(density, size),
          'items-center justify-between text-left hover:border-focus',
          error && 'border-status-danger-fg focus:border-status-danger-fg',
          disabled && 'cursor-not-allowed opacity-50',
          selectedOption ? 'text-text-heading' : 'text-text-muted',
        )}
      >
        <span className="truncate">{selectedOption ? selectedOption.label : placeholder}</span>
        <ChevronDown
          className={cn(
            'ml-2 h-4 w-4 shrink-0 text-text-muted transition-transform',
            isOpen && 'rotate-180',
          )}
        />
      </Button>
      {listbox}
    </div>
  );
}
